import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";
import { isAdmin } from "@/entities/user";
import { Skeleton } from "@/shared/ui";
import { useSession } from "../api/session";

function SessionLoading() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t("common.loading")} className="p-8">
      <Skeleton className="h-6 w-48" />
    </div>
  );
}

/** Renders nothing protected until /auth/me has resolved, so there is no flash of protected content. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <SessionLoading />;
  if (session.isError) {
    return (
      <p role="alert" className="p-8">
        {t("auth.serverUnreachable")}
      </p>
    );
  }
  if (!session.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

export function RequireAdmin({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const session = useSession();
  if (session.isPending) return <SessionLoading />;
  return isAdmin(session.data) ? children : fallback;
}
